import React, { useState } from 'react';
import { useTheme } from '../../lib/theme';
import { useAuth } from '../../lib/auth';
import { updateProfile } from '../../lib/api';
import TaxSettings from '../TaxSettings';
import ChangePasswordModal from './ChangePasswordModal';
import ComingSoonModal from '../landing/ComingSoonModal';
import { SectionShell, SettingsCard, textInput, primaryBtn, outlineBtn } from './_shared';

export default function ProfileSection({ onToast }) {
  const { colors } = useTheme();
  const { user, updateUser } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || '');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveDisplayName = async () => {
    const trimmed = (displayName || '').trim();
    if (!trimmed) return;
    if (trimmed === (user?.display_name || '')) { setEditing(false); return; }
    setBusy(true);
    try {
      const updated = await updateProfile({ display_name: trimmed });
      updateUser(updated);
      setDisplayName(updated.display_name);
      onToast?.('Display name updated');
      setEditing(false);
    } catch (e) {
      onToast?.(e?.detail || 'Could not update display name');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell eyebrow="Profile" title="Profile" description="Your login, name, and business structure." testid="section-profile">
      <SettingsCard title="Login settings" description="Your account email. Email changes are coming soon." testid="profile-login-card">
        <div style={{ padding: 12, borderRadius: 10, background: colors.accentSoft, color: colors.text, fontSize: 14, fontWeight: 600 }} data-testid="profile-email">
          {user?.email}
        </div>
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={() => setPwOpen(true)} data-testid="change-password-btn" style={outlineBtn(colors)}>Change password</button>
        </div>
      </SettingsCard>

      <SettingsCard title="Reset password" description="Lost your password? We can email you a reset link." testid="profile-reset-card">
        <button type="button" onClick={() => setResetOpen(true)} data-testid="reset-password-btn" style={outlineBtn(colors)}>
          Send reset email
        </button>
      </SettingsCard>

      <SettingsCard title="Display name" description="How your name appears in the app." testid="profile-displayname-card">
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              maxLength={60} data-testid="displayname-input"
              style={{ ...textInput(colors), flex: 1 }}
            />
            <button type="button" onClick={saveDisplayName} disabled={busy} data-testid="displayname-save" style={primaryBtn(colors, busy)}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => { setDisplayName(user?.display_name || user?.name || ''); setEditing(false); }} style={outlineBtn(colors)}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, color: colors.text, fontSize: 14, fontWeight: 600 }} data-testid="displayname-value">{user?.display_name || user?.name}</div>
            <button type="button" onClick={() => setEditing(true)} data-testid="displayname-edit" style={outlineBtn(colors)}>Edit</button>
          </div>
        )}
      </SettingsCard>

      <SettingsCard title="Business Structure" description="Province, structure, year-end, and filing frequency drive the Canadian tax engine." testid="profile-business-card">
        <TaxSettings />
      </SettingsCard>

      {pwOpen && (
        <ChangePasswordModal
          onClose={() => setPwOpen(false)}
          onDone={() => { setPwOpen(false); onToast?.('Password updated'); }}
        />
      )}
      {resetOpen && (
        <ComingSoonModal
          title="Full password reset coming soon"
          body="Full password reset email flow coming soon. In the meantime, use Change password above or contact support@clasifi.ca."
          onClose={() => setResetOpen(false)}
        />
      )}
    </SectionShell>
  );
}
